import { Accordion, ActionIcon, Divider, Tooltip } from '@mantine/core';
import { Trash2 } from 'lucide-react';
import { useState } from 'react';

import { DeleteAppointment } from '@/components/AppointmentScheduler/delete-appointment.tsx';
import Icon from '@/components/Icon.tsx';
import { CollapsedAppointment } from '@/components/PatientModal/collapsed-appointment.tsx';

import { Appointment, CollectionActionScope, DailyAppointment } from '@/models/appointment/Appointment.ts';
import { CancelAppointmentOptions } from '@/models/appointment/CancelAppointmentOptions.ts';
import type { ProfessionalProfileDocument } from '@/models/prismicTypes.ts';

import { buildChainFutureCountMap, isCanceledAppointment, titleAfterId } from '@/utils/appointmentUtils.ts';
import { dayjs } from '@/utils/dayjsSetup.ts';
import { getTextFromRichTextField } from '@/utils/prismicUtils.ts';
import { cn } from '@/utils/styleUtils.ts';

type OnDelete = (
    appointment: Appointment,
    scope: CollectionActionScope | undefined,
    cancelOption: CancelAppointmentOptions
) => void;

interface Props {
    groupedAppointments: Record<string, Appointment[]>;
    professionalProfiles: Map<string, ProfessionalProfileDocument['data']> | undefined;
    onEdit: (apt: Appointment) => void;
    onDelete: OnDelete;
    onAppointmentClick?: (appointment: DailyAppointment) => void;
    isSeriesUnfinished?: boolean;
    futureAppointments?: Appointment[];
}

interface CollectionGroupProps {
    groupKey: string;
    appointmentsInGroup: Appointment[];
    practitionerName: string;
    isSeriesUnfinished?: boolean;
    chainFutureAppointmentCount: number;
    isDeleting: boolean;
    onEdit: (apt: Appointment) => void;
    onDelete: OnDelete;
    onAppointmentClick?: (appointment: DailyAppointment) => void;
    onStartDelete: () => void;
    onCancelDelete: () => void;
}

const CollectionGroup = ({
    groupKey,
    appointmentsInGroup,
    practitionerName,
    isSeriesUnfinished,
    chainFutureAppointmentCount,
    isDeleting,
    onEdit,
    onDelete,
    onAppointmentClick,
    onStartDelete,
    onCancelDelete,
}: CollectionGroupProps) => {
    const appointment = appointmentsInGroup[0];
    const isChain = Boolean(appointment.chain_configuration_id);
    const chainName = isChain ? appointment.collection_data?.chain_name?.de : undefined;
    const startDate = appointmentsInGroup[0]?.start;
    const endDate = appointmentsInGroup.at(-1)?.end;
    const formattedStart = startDate ? dayjs(startDate).format('DD.MM.YY') : '';
    const formattedEnd = endDate ? dayjs(endDate).format('DD.MM.YY') : '';
    const canceledAppointmentsCount = appointmentsInGroup.filter((apt) => isCanceledAppointment(apt)).length;
    const canceledInfo = canceledAppointmentsCount > 0 ? `(${canceledAppointmentsCount} abgesagt)` : '';
    const groupHeading = chainName ?? titleAfterId(appointment.name);

    const handleDeleteFromPanel = (
        scope: CollectionActionScope | undefined,
        reason: CancelAppointmentOptions
    ): void => {
        onCancelDelete();
        onDelete(appointment, scope, reason);
    };

    return (
        <Accordion.Item
            key={groupKey}
            value={groupKey}
            className={cn(
                'w-full rounded-lg border border-l-4 px-2 py-2',
                isChain ? 'border-l-blue-500' : 'border-l-violet-500'
            )}
        >
            <div className="flex items-center">
                <Accordion.Control className="flex-1">
                    <div>
                        {isChain ? groupHeading : `${groupHeading} | ${practitionerName}`} <br />
                        {`${formattedStart} - ${formattedEnd} | ${appointmentsInGroup.length} Termine`}
                        {canceledAppointmentsCount > 0 && <span className="text-red-600"> {canceledInfo} </span>}
                    </div>
                </Accordion.Control>
                {isChain && chainFutureAppointmentCount > 0 && (
                    <Tooltip label="Terminkette stornieren">
                        <ActionIcon
                            variant="transparent"
                            aria-label="delete-chain-icon"
                            onClick={onStartDelete}
                            disabled={isDeleting}
                            className="group mr-2 bg-transparent"
                        >
                            <Icon icon={Trash2} color="text-red-600 hover:text-red-500 group-disabled:text-gray-400" />
                        </ActionIcon>
                    </Tooltip>
                )}
            </div>
            <Accordion.Panel>
                {isDeleting && isChain ? (
                    <>
                        <Divider className="mb-2 w-full" />
                        <DeleteAppointment
                            onDelete={handleDeleteFromPanel}
                            onCancel={onCancelDelete}
                            isChainMode
                            chainFutureAppointmentCount={chainFutureAppointmentCount}
                        />
                    </>
                ) : (
                    appointmentsInGroup.map((apt) => (
                        <CollapsedAppointment
                            key={apt.id}
                            noSideBorders
                            isSeriesUnfinished={isSeriesUnfinished}
                            appointment={apt}
                            // Chain edit is not available yet
                            onEdit={isChain ? undefined : onEdit}
                            // Chain cancellation happens from the group header only
                            onDelete={isChain ? undefined : onDelete}
                            onAppointmentClick={onAppointmentClick}
                            practitionerName={practitionerName}
                        />
                    ))
                )}
            </Accordion.Panel>
        </Accordion.Item>
    );
};

export const GroupedAppointments = ({
    groupedAppointments,
    professionalProfiles,
    onEdit,
    onDelete,
    isSeriesUnfinished,
    onAppointmentClick,
    futureAppointments,
}: Props) => {
    const chainFutureCountMap = buildChainFutureCountMap(futureAppointments);
    const [openedGroups, setOpenedGroups] = useState<string[]>([]);
    const [deletingGroupKey, setDeletingGroupKey] = useState<string | undefined>(undefined);

    const handleAccordionChange = (newOpenedGroups: string[]): void => {
        if (deletingGroupKey !== undefined && !newOpenedGroups.includes(deletingGroupKey)) {
            setDeletingGroupKey(undefined);
        }
        setOpenedGroups(newOpenedGroups);
    };

    return (
        <Accordion multiple value={openedGroups} onChange={handleAccordionChange}>
            {Object.entries(groupedAppointments).map(([key, appointmentsInGroup]) => {
                if (appointmentsInGroup.length === 0) return undefined;

                const appointment = appointmentsInGroup[0];
                const isCollection = Boolean(appointment.collection_id);
                const practitionerProfile = professionalProfiles?.get(appointment.professional.key);
                const practitionerName =
                    practitionerProfile && practitionerProfile.display_name
                        ? getTextFromRichTextField(practitionerProfile.display_name)
                        : appointment.professional.key;

                if (isCollection) {
                    return (
                        <CollectionGroup
                            key={key}
                            groupKey={key}
                            appointmentsInGroup={appointmentsInGroup}
                            practitionerName={practitionerName}
                            isSeriesUnfinished={isSeriesUnfinished}
                            chainFutureAppointmentCount={chainFutureCountMap.get(appointment.collection_id ?? '') ?? 0}
                            isDeleting={deletingGroupKey === key}
                            onEdit={onEdit}
                            onDelete={onDelete}
                            onAppointmentClick={onAppointmentClick}
                            onStartDelete={() => {
                                setDeletingGroupKey(key);
                                setOpenedGroups((prev) => (prev.includes(key) ? prev : [...prev, key]));
                            }}
                            onCancelDelete={() => setDeletingGroupKey(undefined)}
                        />
                    );
                }

                return (
                    <CollapsedAppointment
                        key={appointment.id}
                        appointment={appointment}
                        onEdit={onEdit}
                        onDelete={onDelete}
                        onAppointmentClick={onAppointmentClick}
                        practitionerName={practitionerName}
                    />
                );
            })}
        </Accordion>
    );
};
